import { useState, useEffect, useRef, useCallback } from 'react';
import EnvHelper from '../helpers/envHelper';

/**
 * Hook สำหรับใช้ Orders Metrics Worker
 *
 * @param {Array} orders - รายการ orders
 * @param {Object} options - ตัวเลือก
 * @param {boolean} options.enabled - เปิด/ปิดการใช้งาน worker (default: true)
 * @param {number} options.debounceMs - debounce delay (default: 100)
 * @returns {Object} { metrics, isCalculating, error, duration }
 */
export function useOrdersMetricsWorker(orders = [], options = {}) {
  const { enabled = true, debounceMs = 100 } = options;

  const [metrics, setMetrics] = useState({
    failled: 0,
    canceled: 0,
    expired: 0,
    total: 0
  });

  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);

  const workerRef = useRef(null);
  const messageIdRef = useRef(0);
  const pendingCallbacksRef = useRef(new Map());
  const debounceTimerRef = useRef(null);

  // สร้าง worker
  useEffect(() => {
    if (!enabled) return;

    try {
      // สร้าง worker จากไฟล์
      const envHelper = EnvHelper.getInstance();
      const workerUrl = new URL('../workers/ordersMetrics.worker.js', import.meta.url);
      const fixedUrl = envHelper.fixUrl(workerUrl);
      console.log('[OrdersMetrics] Creating worker with URL:', fixedUrl);
      
      workerRef.current = new Worker(fixedUrl, { type: 'module' });

      // รับ message จาก worker
      workerRef.current.onmessage = (e) => {
        const { type, id, payload } = e.data;

        switch (type) {
          case 'WORKER_READY':
            console.log('📋 Orders Metrics Worker ready');
            break;

          case 'ORDERS_METRICS_RESULT': {
            const callback = pendingCallbacksRef.current.get(id);
            if (callback) {
              callback(null, payload);
              pendingCallbacksRef.current.delete(id);
            }
            break;
          }

          case 'ERROR': {
            const callback = pendingCallbacksRef.current.get(id);
            if (callback) {
              callback(new Error(payload.message), null);
              pendingCallbacksRef.current.delete(id);
            }
            break;
          }

          case 'PONG':
            console.log('📋 Worker health check: OK');
            break;

          default:
            console.warn('Unknown message type from worker:', type);
        }
      };

      // จัดการ error
      workerRef.current.onerror = (err) => {
        console.error('📋 Worker error:', err);
        setError(err.message);
        setIsCalculating(false);
      };
    } catch (err) {
      console.error('Failed to create worker:', err);
      setError(err.message);
    }

    // Cleanup
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      pendingCallbacksRef.current.clear();
    };
  }, [enabled]);

  // ส่ง message ไปยัง worker
  const postMessage = useCallback((type, payload) => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        reject(new Error('Worker not initialized'));
        return;
      }

      const id = messageIdRef.current++;

      // เก็บ callback
      pendingCallbacksRef.current.set(id, (error, result) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      });

      // ส่ง message
      workerRef.current.postMessage({ type, payload, id });
    });
  }, []);

  // คำนวณ metrics
  const calculateMetrics = useCallback(
    async (ordersList) => {
      if (!enabled || !workerRef.current) {
        // Fallback: คำนวณใน main thread
        return calculateMetricsSync(ordersList);
      }

      try {
        setIsCalculating(true);
        setError(null);

        const result = await postMessage('CALCULATE_ORDERS_METRICS', {
          orders: ordersList
        });

        setMetrics(result.metrics);
        setDuration(result.duration);
        setIsCalculating(false);

        console.log(
          `📊 Orders metrics calculated in worker: ${result.duration.toFixed(2)}ms for ${
            result.itemCount
          } items`
        );

        return result.metrics;
      } catch (err) {
        console.error('Failed to calculate orders metrics in worker:', err);
        setError(err.message);
        setIsCalculating(false);

        // Fallback
        return calculateMetricsSync(ordersList);
      }
    },
    [enabled, postMessage]
  );

  // คำนวณเมื่อ orders เปลี่ยน (with debounce)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // ถ้าไม่มีข้อมูล reset metrics
    if (!orders || orders.length === 0) {
      setMetrics({
        failed: 0,
        canceled: 0,
        expired: 0,
        total: 0
      });
      setIsCalculating(false);
      return;
    }

    // Set calculating state ทันที เพื่อให้ consumer รู้ว่ากำลังคำนวณ
    setIsCalculating(true);
    setError(null);

    // Debounce calculation
    debounceTimerRef.current = setTimeout(() => {
      calculateMetrics(orders);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [orders, debounceMs, calculateMetrics]);

  // Health check
  const ping = useCallback(async () => {
    if (!workerRef.current) return false;

    try {
      await postMessage('PING', {});
      return true;
    } catch {
      return false;
    }
  }, [postMessage]);

  return {
    metrics,
    isCalculating,
    error,
    duration,
    ping,
    calculateMetrics // manual trigger
  };
}

// Fallback: คำนวณใน main thread
function calculateMetricsSync(orders) {
  if (!orders || orders.length === 0) {
    return {
      failed: 0,
      canceled: 0,
      expired: 0,
      total: 0
    };
  }

  const ORDER_STATES = {
    FAILED: 4,
    CANCELED: 2,
    EXPIRED: 6
  };

  return orders.reduce(
    (acc, order) => {
      if (order.state === ORDER_STATES.FAILED) {
        acc.failed++;
      } else if (order.state === ORDER_STATES.CANCELED) {
        acc.canceled++;
      } else if (order.state === ORDER_STATES.EXPIRED) {
        acc.expired++;
      }

      if (order.state !== undefined) {
        acc.total++;
      }

      return acc;
    },
    {
      failed: 0,
      canceled: 0,
      expired: 0,
      total: 0
    }
  );
}

export default useOrdersMetricsWorker;
